import {
	Alert,
	AlertIcon,
	Button,
	HStack,
	IconButton,
	Link,
	Select,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr
} from "@chakra-ui/react";
import { MultiValue } from "chakra-react-select";
import { cloneDeep } from "lodash";
import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from "react";
import { MdDelete } from "react-icons/md";

// Components
import DetailField from "../../../shared/DetailElement/DetailField";
import CustomModal from "../../../shared/Modals/CustomModal";
import StepperFooter from "../../../shared/StickyFooter/footers/StepperFooter";
import BenefitDefinitionsModal from "../detail/BenefitDefinitionsModal";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { ClaimServiceProvidedFor, DocumentCategory, claimsRoles } from "../../../utils/consts";
import { Claim, ClaimDocument, GroupSelectOption } from "../../../utils/types";

// Protos
import { useClaim } from "../../../context-providers/claims-provider";
import { LegalBenefit as BffLegalBenefit } from "../../../utils/api/protos/messages/benefits.message_pb";
import { generateCcsPdf } from "../../../utils/api/pdf";
import { RpcError } from "grpc-web";

interface ServicesProps {
	/**
	 * Delegate to handle logic when User clicks the Previous button
	 * @returns void
	 */
	handleStepPrevious: () => void;

	handleStepClick: (index: number) => void;
	currentStep: number;
	/** If the component is in admin mode, where services & billing are edited together */
	isInAdminMode?: boolean;
}

/**
 * Handles the logic and workflow of the 2nd step of Claims Creation.
 * The first row's benefit selection is free-form (full list), but if the first
 * benefit is estate planning then additional rows can be added, and those rows
 * will only show estate planning options.
 *
 * @param props typeof: ServicesProps
 * @returns ReactElement
 */
const Services: FunctionComponent<ServicesProps> = (props: ServicesProps): ReactElement => {
	// Props
	const { handleStepPrevious, handleStepClick, currentStep, isInAdminMode } = props;
	const {
		claim,
		localClaim,
		localBenefits,
		benefitsList,
		handleClaimUpsert,
		handleServiceDelete,
		setClaim,
		setLocalClaim,
		setBillHourlyAsFlat,
		setBillFlatAsHourly,
		setCcsGenerationStatus,
		setCcsGenerationError
	} = useClaim();

	// State
	const [legalBenefitsList, setLegalBenefitsList] = useState<GroupSelectOption[]>();
	const [isBillingTypeMissing, setIsBillingTypeMissing] = useState<boolean>();
	const [showCustomModal, setShowCustomModal] = useState(false);
	const [showBenefitDefinitionModal, setShowBenefitDefinitionModal] = useState(false);
	const [serviceToRemove, setServiceToRemove] = useState<number | null>(null);

	const { getUser } = useAuth();
	const currentUser = getUser();
	const toast = useClaimsToast();

	// Initialize service on a new claim
	useEffect(() => {
		if (!localClaim?.claimServices?.length) {
			setLocalClaim({
				...localClaim,
				claimServices: [{ legalBenefitId: undefined, claimId: claim.claimId, serviceProvidedFor: undefined }]
			});
		}
	}, []);

	// Determine if the "Next" button should be disabled.
	const isNextDisabled = useMemo(() => {
		let isClaimsUser: boolean = false;
		currentUser?.role.split(",").forEach((role: string) => {
			if (claimsRoles.includes(role)) {
				isClaimsUser = true;
			}
		});
		return (
			localClaim.claimServices.length === 0 ||
			localClaim.claimServices.some((service) => {
				if (!isClaimsUser) {
					return (
						!service.legalBenefitId ||
						!service.serviceProvidedFor ||
						(service.serviceProvidedFor === ClaimServiceProvidedFor.DependentChild && !service.recipientAge)
					);
				} else {
					return !service.legalBenefitId || !service.serviceProvidedFor;
				}
			})
		);
	}, [localClaim]);

	/**
	 * Handles adding a service to the current working copy
	 * Requires legalBenefit, serviceProvidedFor, and Claim ID to be non-empty
	 */
	const handleAddService = (): void => {
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.concat({
				legalBenefitId: undefined,
				claimId: claim.claimId,
				serviceProvidedFor:
					localClaim.claimServices.length > 0
						? localClaim.claimServices[localClaim.claimServices.length - 1].serviceProvidedFor
						: undefined,
				recipientAge: undefined
			})
		});
	};

	// Handle changes to a benefit selection.
	const handleBenefitChange = (serviceIndex: number, data: MultiValue<{ label: string; value: string }>): void => {
		const matterBillingCode = data[0].value;
		// Find the new benefit
		const foundBenefit = benefitsList?.find(
			(masterBenefit: BffLegalBenefit) => masterBenefit.getMatterBillingCode() === matterBillingCode
		);
		// On update of a service benefit, clear all billing items so there are no invalid billing items
		if (foundBenefit && localClaim.claimServices.length > serviceIndex) {
			setLocalClaim({
				...localClaim,
				claimServices: localClaim.claimServices.map((item, i) =>
					i === serviceIndex
						? {
								...item,
								claimId: localClaim.claimId!,
								legalBenefitId: foundBenefit.getId(),
								serviceProvidedFor: item.serviceProvidedFor,
								recipientAge: undefined,
								serviceId: undefined,
								billing: item.billing && undefined
							}
						: item
				)
			});
			// Clear billing conversions as well
			setBillHourlyAsFlat(false);
			setBillFlatAsHourly(false);
		}
	};

	// Track which services have recipients selected (for display purposes only, no uniqueness constraint)
	const selectedBenefitRecipients = useMemo(() => {
		return localClaim.claimServices
			.filter((item) => item.serviceProvidedFor !== undefined)
			.map((item) => item.serviceProvidedFor?.toString() ?? "");
	}, [localClaim]);

	// Handle changes to the recipient select.
	const handleRecipientChange = (index: number, value: ClaimServiceProvidedFor | undefined): void => {
		if (localClaim.claimServices.length > index) {
			setLocalClaim({
				...localClaim,
				claimServices: localClaim.claimServices.map((item, i) =>
					i === index
						? {
								...item,
								serviceProvidedFor: value,
								recipientAge: undefined
							}
						: item
				)
			});
		}
	};

	// Handle changes to the recipient select.
	const handleRecipientAgeChange = (index: number, value: number | undefined): void => {
		if (localClaim.claimServices.length > index) {
			setLocalClaim({
				...localClaim,
				claimServices: localClaim.claimServices.map((item, i) =>
					i === index
						? {
								...item,
								recipientAge: value
							}
						: item
				)
			});
		}
	};

	/**
	 * Handles the logic when the "Next" button is clicked
	 * Upserts Claim and proceeds to next step
	 */
	const handleNextClick = async () => {
		try {
			const workingCopy: Claim = cloneDeep(localClaim);
			let isSuccess = await handleClaimUpsert(workingCopy, false);
			if (isSuccess) {
				// Generate CCS PDF after successful claim upsert (NON-BLOCKING - background generation)
				if (claim?.claimId) {
					setCcsGenerationStatus("loading");
					setCcsGenerationError(null);
					toast("Info", "Claim form generation started in background", "info", 3000);

					// Fire and forget - don't await
					generateCcsPdf(claim.claimId)
						.then((response) => {
							// Extract file info from response
							const pdfUrl = response.getUrl();
							const fileId = response.getFileId();
							const fileName = response.getFilename();
							const fileSize = response.getSizeBytes();

							if (pdfUrl && fileId && fileName) {
								// Create the main CCS document
								const ccsDocuments: ClaimDocument[] = [
									{
										file: {
											fileId: fileId,
											fileName: fileName,
											fileSize: fileSize
										},
										category: DocumentCategory.Ccs,
										isDeleted: false,
										fileSize: fileSize,
										isGenerated: true
									}
								];

								// Add any additional PDFs if they exist
								const additionalPdfs = response.getAdditionalPdfsList();
								if (additionalPdfs && additionalPdfs.length > 0) {
									additionalPdfs.forEach((pdf) => {
										ccsDocuments.push({
											file: {
												fileId: pdf.getFileId(),
												fileName: pdf.getFilename(),
												fileSize: pdf.getSizeBytes()
											},
											category: DocumentCategory.Ccs,
											isDeleted: false,
											fileSize: pdf.getSizeBytes(),
											isGenerated: true
										});
									});
								}

								// Remove existing auto-generated CCS documents and add the new ones
								// Use functional update to ensure we have latest state
								setClaim((prevClaim) => ({
									...prevClaim,
									documents: [
										...(prevClaim.documents || []).filter(
											(doc) => !(doc.category === DocumentCategory.Ccs && doc.isGenerated === true)
										),
										...ccsDocuments
									]
								}));
								setLocalClaim((prevClaim) => ({
									...prevClaim,
									documents: [
										...(prevClaim.documents || []).filter(
											(doc) => !(doc.category === DocumentCategory.Ccs && doc.isGenerated === true)
										),
										...ccsDocuments
									]
								}));

								const pdfCount = response.getTotalPdfCount() || ccsDocuments.length;
								toast("Success", `${pdfCount} Claim form${pdfCount > 1 ? "s" : ""} generated successfully`, "success");
								setCcsGenerationStatus("success");
							} else {
								toast(
									"Warning",
									"PDF generation completed but file info is incomplete. You can retry on step 3.",
									"warning",
									6000
								);
								setCcsGenerationStatus("error");
								setCcsGenerationError("File info incomplete");
							}
						})
						.catch((err) => {
							// Show more informative error message with retry instructions
							const errorMessage = (err as RpcError)?.message || "Unknown error occurred";
							toast(
								"Error",
								`Claim form generation failed: ${errorMessage}. You can retry generation on step 3 (Documents).`,
								"error",
								8000
							);
							console.error("Error generating PDF:", err);
							setCcsGenerationStatus("error");
							setCcsGenerationError(errorMessage);
						});
				}
				// Proceed to next step immediately (don't wait for PDF)
				handleStepClick(currentStep + 1);
			}
		} catch (e) {
			console.log("error in next click", e);
		}
	};

	/**
	 * Handles the logic when the "Previous" button is clicked
	 * saves the current working copy and decrements the current step
	 * Calls delegate to decrement current step
	 * @returns void
	 */
	const handlePreviousClick = async (): Promise<void> => {
		try {
			const workingCopyClone: Claim = cloneDeep(localClaim);
			let isSuccess = await handleClaimUpsert(workingCopyClone, false);
			if (isSuccess) handleStepPrevious();
		} catch (e) {
			console.log("error in previous click", e);
		}
	};

	/**
	 * Handles the logic when the User removes an added Service
	 * Updates the working copy, persistence happens on "Next" click
	 * Will not persist changes between tab changes
	 * @param index Index of the Service to be removed
	 */
	const handleRemoveService = (index: number): void => {
		const serviceToRemove = localClaim.claimServices[index];
		if (serviceToRemove && serviceToRemove.serviceId && claim.claimId) {
			handleServiceDelete(claim.claimId, serviceToRemove.serviceId);
		}
		setLocalClaim({
			...localClaim,
			claimServices: localClaim.claimServices.filter((_, i) => i !== index)
		});
		setShowCustomModal(false);
		setServiceToRemove(null);
	};

	/**
	 * Either shows the confirmation modal and stores the index of the service to remove, or immediately deletes local service
	 * @param index Index of the service to remove
	 */
	const handleRemoveServiceClick = (index: number) => {
		// If in admin mode, immediately process local delete
		if (isInAdminMode) {
			setLocalClaim({
				...localClaim,
				claimServices: localClaim.claimServices.filter((_, i) => i !== index)
			});
		} else {
			// Otherwise prompt for permanent deletion
			setServiceToRemove(index);
			setShowCustomModal(true);
		}
	};

	/**
	 * Close the confirmation modal without removing the service
	 */
	const handleCancelRemove = () => {
		setShowCustomModal(false);
		setServiceToRemove(null);
	};

	/**
	 * Confirms the removal of the service
	 */
	const handleConfirmRemove = () => {
		if (serviceToRemove !== null) {
			handleRemoveService(serviceToRemove);
		}
	};

	/**
	 * 	Creates a grouped renderable list of legal benefits
	 */
	useEffect(() => {
		if (!benefitsList) {
			setLegalBenefitsList([]);
			return;
		}

		const groupsMap = new Map<string, { label: string; value: string }[]>();
		const nonGrouped: { label: string; value: string }[] = [];
		const missingBillingTypeCodes: string[] = [];

		benefitsList.forEach((item: BffLegalBenefit) => {
			const groupTitle = item.getGroupTitle();
			const matterTitle = `${item.getMatterBillingCode()} ${item.getMatterTitle()}`;
			const matterBillingCode = item.getMatterBillingCode();
			const hasBillingType = item.hasBillingtype();

			if (!hasBillingType) {
				missingBillingTypeCodes.push(matterBillingCode);
			}

			if (!groupTitle && hasBillingType) {
				nonGrouped.push({ label: matterTitle, value: matterBillingCode });
			} else if (hasBillingType) {
				if (!groupsMap.has(groupTitle)) {
					groupsMap.set(groupTitle, []);
				}
				groupsMap.get(groupTitle)?.push({ label: matterTitle, value: matterBillingCode });
			}
		});

		const draftList: GroupSelectOption[] = [];
		groupsMap.forEach((options, groupTitle) => {
			draftList.push({
				label: groupTitle,
				value: groupTitle,
				options
			});
		});
		if (nonGrouped.length > 0) {
			draftList.push({
				label: "Other",
				value: "non-grouped",
				options: nonGrouped
			});
		}

		// Alert user of missing billing types
		if (missingBillingTypeCodes.length) {
			setIsBillingTypeMissing(true);
		} else {
			setLegalBenefitsList(draftList);
		}
	}, [benefitsList]);

	// For additional rows (index > 0), filter options to only estate planning benefits.
	const filteredLegalBenefitsList = useMemo(() => {
		if (!legalBenefitsList || !benefitsList) return legalBenefitsList;
		const newGroups = legalBenefitsList
			.map((group) => {
				const newOptions = group.options.filter((option) => {
					const benefit = benefitsList.find((b: BffLegalBenefit) => b.getMatterBillingCode() === option.value);
					return benefit && benefit.getIsestateplanning();
				});
				return { ...group, options: newOptions };
			})
			.filter((group) => group.options.length > 0);
		return newGroups;
	}, [legalBenefitsList, benefitsList]);

	// Only allow adding more rows if the first service's benefit is estate planning.
	const canAddMoreServices = useMemo(() => {
		if (!benefitsList || !localClaim.claimServices[0]?.legalBenefitId) return false;
		const firstBenefit = benefitsList.find(
			(b: BffLegalBenefit) => b.getId() === localClaim.claimServices[0].legalBenefitId
		);
		return firstBenefit ? firstBenefit.getIsestateplanning() : false;
	}, [localClaim, benefitsList]);

	return (
		<>
			<BenefitDefinitionsModal
				benefits={localBenefits}
				isOpen={showBenefitDefinitionModal}
				onContinue={() => setShowBenefitDefinitionModal(false)}
			/>
			<CustomModal
				isOpen={showCustomModal}
				size="xl"
				message="Are you sure you want to remove this service?"
				continueText="Confirm"
				onContinue={handleConfirmRemove}
				onCancel={handleCancelRemove}
			/>
			{isBillingTypeMissing && (
				<Alert status="warning" mb={5}>
					<AlertIcon />
					<Text>
						Some benefits are missing a Billing Type. Please contact our claims support team at{" "}
						<Link color="orange.500" href="mailto:claimshelpdesk@legaleaseplan.com">
							claimshelpdesk@legaleaseplan.com
						</Link>{" "}
						to address this issue.
					</Text>
				</Alert>
			)}
			<TableContainer>
				<Table variant="unstyled" size="xs">
					<Thead borderRadius="4px" backgroundColor="gray.300">
						<Tr h="44px">
							<Th
								minWidth="40%"
								textTransform="none"
								fontSize="1rem"
								fontWeight="500"
								borderLeftRadius="4px"
								pl={2}
								pr={3}>
								Service Legal Benefit{canAddMoreServices ? "s" : ""}
							</Th>
							<Th minWidth="50%" textTransform="none" fontSize="1rem" fontWeight="500">
								Recipient
							</Th>
							{selectedBenefitRecipients.join(",").includes(ClaimServiceProvidedFor.DependentChild) && (
								<Th minWidth="50%" textTransform="none" fontSize="1rem" fontWeight="500">
									Age
								</Th>
							)}
							<Th minWidth="10%" borderRightRadius="4px"></Th>
						</Tr>
					</Thead>
					<Tbody mt="5">
						{localClaim.claimServices.map((svc, scvIndex) => {
							const foundBenefit = benefitsList?.find(
								(masterBenefit: BffLegalBenefit) => masterBenefit.getId() === svc.legalBenefitId
							);

							// For the first row, use the full list; for additional rows, restrict to estate planning options.
							const optionsForRow = scvIndex === 0 ? legalBenefitsList : filteredLegalBenefitsList;
							return (
								<Tr key={scvIndex}>
									<Td pl={2} pr={3}>
										<DetailField
											displayValue={foundBenefit?.getMatterTitle() || ""}
											selectedOptions={[
												{
													label: `${foundBenefit?.getMatterBillingCode() || ""} ${foundBenefit?.getMatterTitle() || ""}`,
													value: foundBenefit?.getMatterBillingCode() ?? ""
												}
											]}
											options={optionsForRow}
											isEdit={true}
											type="chakra-select"
											onMultiChange={(values) => handleBenefitChange(scvIndex, values)}
										/>
									</Td>
									<Td pr={3}>
										<Select
											size="sm"
											variant="flushed"
											fontSize="1rem"
											borderColor={"gray.400"}
											_hover={{ borderColor: "gray.300" }}
											onChange={(event) =>
												handleRecipientChange(scvIndex, event.target.value as ClaimServiceProvidedFor | undefined)
											}
											value={svc.serviceProvidedFor ?? ""}
											marginBottom="8px"
											borderRadius="3px">
											<option key={""} value={""}>
												&nbsp;
											</option>
											{Object.values(ClaimServiceProvidedFor)
												.filter(
													(value) =>
														value !== ClaimServiceProvidedFor.Other || foundBenefit?.getMatterBillingCode() === "790M"
												)
												.map((value) => (
													<option key={value} value={value}>
														{value}
													</option>
												))}
										</Select>
									</Td>
									{selectedBenefitRecipients.join(",").includes(ClaimServiceProvidedFor.DependentChild) && (
										<Td width="100px">
											<DetailField
												displayValue="Age"
												hidden={svc.serviceProvidedFor !== ClaimServiceProvidedFor.DependentChild}
												isEdit={true}
												variant="flushed"
												type="number"
												fontSize="1rem"
												maxW="100px"
												minW="80px"
												marginBottom="8px"
												value={svc.recipientAge?.toString().length ? svc.recipientAge : ""}
												precision={2}
												onNumberChange={(v: number) => handleRecipientAgeChange(scvIndex, v)}
											/>
										</Td>
									)}
									<Td verticalAlign="top">
										{(scvIndex > 0 || (isInAdminMode && canAddMoreServices)) && (
											<IconButton
												minW="32px"
												minH="32px"
												height="32px"
												colorScheme="blackAlpha"
												variant="ghost"
												aria-label="Remove Service"
												onClick={() => handleRemoveServiceClick(scvIndex)}
												icon={<MdDelete />}
											/>
										)}
									</Td>
								</Tr>
							);
						})}
						{localClaim.claimServices.length === 0 && (
							<Tr height="44px">
								<Td colSpan={5}>
									<Text textAlign="left" color="orange.500" fontStyle="italic">
										There must be at least one service
									</Text>
								</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</TableContainer>
			{canAddMoreServices && (
				<HStack justifyContent="start" mb="5">
					<Button colorScheme="blackAlpha" size="sm" onClick={handleAddService}>
						Add Another
					</Button>
				</HStack>
			)}
			{localBenefits.length > 0 && (
				<Button
					color="orange.500"
					variant="ghost"
					padding="0"
					mb="8"
					mt="2"
					__css={{ background: "transparent", fontWeight: 500 }}
					onClick={() => setShowBenefitDefinitionModal(true)}>
					View Coverages
				</Button>
			)}
			{!isInAdminMode && (
				<StepperFooter
					hasPreviousStep={true}
					hasNextStep={true}
					isNextDisabled={isNextDisabled}
					isSubmit={false}
					onNext={handleNextClick}
					onPrevious={handlePreviousClick}
				/>
			)}
		</>
	);
};

export default Services;
